import setCustomShippingDetails from "@iewc/Store/Handlers/CheckoutReviewAndSubmit/CustomSetShippingDetails";
import { CustomState } from "@iewc/Store/Reducers";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import Zone from "@insite/client-framework/Components/Zone";
import { getReviewAndPayCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/LoadDataIfNeeded";
import notifyProductRecs from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/NotifyProductRecs";
import setCartId from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetCartId";
import setPlaceOrderErrorMessage from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPlaceOrderErrorMessage";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddressErrorModal from "@insite/content-library/Components/AddressErrorModal";
import Page from "@insite/mobius/Page";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    setCartId,
    setPlaceOrderErrorMessage,
    loadDataIfNeeded,
    clearMessages,
    notifyProductRecs,
    setCustomShippingDetails,
};

const mapStateToProps = (state: CustomState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = getReviewAndPayCartState(state);
    const promotionsDataView = getReviewAndPayPromotions(state);
    const expand = state.data.carts.cartApiParameter?.expand || [];

    const isLoadDataNeeded =
        ((!cartState.value ||
            (cartState.value.lineCount && !cartState.value.cartLines?.length) ||
            !cartState.value.carriers ||
            !expand.includes("shipping") ||
            !expand.includes("tax")) &&
            !cartState.isLoading) ||
        (!promotionsDataView.value && !promotionsDataView.isLoading);

    return {
        cartId,
        cartState, // do not delete this, needed to trigger cart loading
        isLoadDataNeeded: isLoadDataNeeded || !getCurrentCountries(state),
        productRecsCalled: state.pages.checkoutReviewAndSubmit.productRecsCalled,
        shipToState: getShipToState(state, cartState.value ? cartState.value.shipToId : undefined),
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const CheckoutReviewAndSubmitPage = ({
    id,
    cartId,
    isLoadDataNeeded,
    productRecsCalled,
    setCartId,
    loadDataIfNeeded,
    setPlaceOrderErrorMessage,
    clearMessages,
    notifyProductRecs,
    // Verndale Customization
    cartState,
    setCustomShippingDetails,
    shipToState,
}: Props) => {
    useEffect(() => {
        if (isLoadDataNeeded) {
            loadDataIfNeeded({ cartId, cartLinesShouldBeExpanded: true });
        } else if (!productRecsCalled) {
            notifyProductRecs({ cartId });
        }
    });

    useEffect(() => {
        setCartId({ cartId });
        setPlaceOrderErrorMessage({});
        clearMessages();
    }, []);

    useEffect(() => {
        let onCalendarFlag = false;
        if (shipToState.value) {
            const { onCalendar } = shipToState.value.properties;

            if (onCalendar != null && (onCalendar.toLowerCase() === "y" || onCalendar.toLowerCase() === "yes")) {
                onCalendarFlag = true;
            }

            if (cartState.value) {
                const {
                    properties: { onCalendarShippingDate, estimatedShippingValidDates },
                } = cartState.value;

                let deliveryDate = onCalendarShippingDate;

                if (estimatedShippingValidDates && estimatedShippingValidDates.length > 0) {
                    const validDates = estimatedShippingValidDates.split(",");
                    deliveryDate = validDates.reduce((prev, curr) => {
                        return prev <= curr ? prev : curr;
                    });
                } else {
                    deliveryDate = convertDateToDateOnlyString(addDays(new Date(), 1));
                }

                const cartLinesEstimateShipDate = cartState.value.cartLines?.map((cartLine: CartLineModel) => {
                    return {
                        id: cartLine.id,
                        estimateShipDate: deliveryDate,
                    };
                });

                setCustomShippingDetails({
                    cartEstimateShipDate: deliveryDate,
                    cartLines: cartLinesEstimateShipDate ?? [],
                });
            }
        }
    }, [cartState, shipToState]);

    return (
        <Page data-test-selector="checkoutReviewAndSubmitPage">
            <Zone zoneName="Content" contentId={id} />
            <AddressErrorModal />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutReviewAndSubmitPage" instead of this constant.
 */
export const CheckoutReviewAndSubmitPageContext = "CheckoutReviewAndSubmitPage";
